import {FC, MouseEvent, useCallback, useRef, useState} from "react";
import Section, {SectionProps} from "../base/Section.tsx";
import IconButton from "@mui/material/IconButton";
import FormatBoldIcon from "@mui/icons-material/FormatBold";
import FormatItalicIcon from "@mui/icons-material/FormatItalic";
import FormatUnderlinedIcon from "@mui/icons-material/FormatUnderlined";
import MenuItem from "@mui/material/MenuItem";
import {Editor, Element} from "slate";
import EditableBox from "./Text/EditableBox.tsx";
import {CustomElementType} from "@memaro/slate";
import {SelectChangeEvent} from "@mui/material/Select/SelectInput";
import InlineSelect from "@memaro/components/InlineSelect.tsx";

export type TextSectionProps = Omit<SectionProps, 'padding' | 'buttons'> & {
    contents: string
    onChange: (sectionID: string, contents: string) => void
}

const TextSection: FC<TextSectionProps> = ({sectionID, onChange, editMode, contents, ...sectionProps}) => {
    const ref = useRef<Editor | null>(null)
    const [selectedElementType, setSelectedElementType] = useState<CustomElementType>()

    const handleBold = useCallback((e: MouseEvent<HTMLElement>) => {
        e.preventDefault()
        e.stopPropagation()
        ref.current?.toggleBold()
    }, [])

    const handleItalic = useCallback((e: MouseEvent<HTMLElement>) => {
        e.preventDefault()
        e.stopPropagation()
        ref.current?.toggleItalic()
    }, [])

    const handleUnderline = useCallback((e: MouseEvent<HTMLElement>) => {
        e.preventDefault()
        e.stopPropagation()
        ref.current?.toggleUnderline()
    }, [])

    const handleStyleChange = useCallback((e: SelectChangeEvent<unknown>) => {
        ref.current?.setElementType(e.target.value as CustomElementType)
        setSelectedElementType(e.target.value as CustomElementType)
    }, [])

    const handleChangeSelection = useCallback(
        (e: Element) => setSelectedElementType(e.type),
        [],
    )

    const handleChange = useCallback(
        (value: string) => onChange(sectionID, value),
        [onChange, sectionID]
    )

    return (
        <Section
            {...sectionProps}
            editMode={editMode}
            sectionID={sectionID}
            padding={10}
            toolbar={(
                <>
                    <InlineSelect value={selectedElementType ?? "paragraph"} onChange={handleStyleChange}>
                        <MenuItem value="h1">Koptekst 1</MenuItem>
                        <MenuItem value="h2">Koptekst 2</MenuItem>
                        <MenuItem value="h3">Koptekst 3</MenuItem>
                        <MenuItem value="paragraph">Paragraaf</MenuItem>
                    </InlineSelect>
                    <IconButton size="small" onMouseDown={handleBold}><FormatBoldIcon/></IconButton>
                    <IconButton size="small" onMouseDown={handleItalic}><FormatItalicIcon/></IconButton>
                    <IconButton size="small" onMouseDown={handleUnderline}><FormatUnderlinedIcon/></IconButton>
                </>
            )}
        >
            <EditableBox
                contents={contents}
                editMode={editMode}
                ref={ref}
                onChange={handleChange}
                onChangeSelection={handleChangeSelection}
            />
        </Section>
    )
}

export default TextSection
